import React from 'react';
import { Box, IconButton, Button, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/**
 * Presentational offset-based pagination component.
 * Parent is responsible for fetching data when onPageChange is called.
 *
 * @param {number} limit - Page size
 * @param {number} offset - Current start index
 * @param {number} total - Total count from API
 * @param {(newOffset: number) => void} onPageChange - Called when user selects a page or clicks Prev/Next
 */
function OffsetPagination({ limit, offset, total, onPageChange }) {
    const totalPages = limit > 0 ? Math.ceil(total / limit) : 0;
    const currentPage = total > 0 ? Math.floor(offset / limit) + 1 : 1;
    const canPrev = offset > 0;
    const canNext = offset + limit < total;

    const handlePrev = () => {
        if (canPrev) onPageChange(offset - limit);
    };

    const handleNext = () => {
        if (canNext) onPageChange(offset + limit);
    };

    const handlePage = (pageNum) => {
        const newOffset = (pageNum - 1) * limit;
        if (pageNum >= 1 && pageNum <= totalPages) onPageChange(newOffset);
    };

    if (totalPages <= 0) return null;

    // Build a reasonable set of page numbers to show (current and neighbors)
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
        startPage = Math.max(1, endPage - maxVisible + 1);
    }
    const pageNumbers = [];
    for (let p = startPage; p <= endPage; p++) pageNumbers.push(p);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', py: 2 }}>
            <IconButton
                size="small"
                onClick={handlePrev}
                disabled={!canPrev}
                aria-label="Previous page"
            >
                <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" sx={{ mx: 1, alignSelf: 'center' }}>
                Page {currentPage} of {totalPages}
            </Typography>
            {pageNumbers.map((p) => (
                <Button
                    key={p}
                    size="small"
                    variant={p === currentPage ? 'contained' : 'outlined'}
                    onClick={() => handlePage(p)}
                    sx={{ minWidth: 36 }}
                >
                    {p}
                </Button>
            ))}
            <IconButton
                size="small"
                onClick={handleNext}
                disabled={!canNext}
                aria-label="Next page"
            >
                <ChevronRightIcon />
            </IconButton>
        </Box>
    );
}

export default OffsetPagination;
